import {
  Avatar,
  Box,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Switch,
  Typography,
} from '@mui/material'
import LogoutIcon from '@mui/icons-material/Logout'
import Brightness6Icon from '@mui/icons-material/Brightness6'
import type { AuthService } from './services/AuthService.ts'
import type { CssThemeService } from '../app-shell/CssThemeService'
import { useDiRef } from '@/common/react/di-hooks'
import { useWrappedObservable } from '@/common/react/observable-hooks'
import { useBehaviorObservable } from '@/common/react/observable-hooks'
import { UsersService } from '@/user/UsersService'
import { AppInfoService } from '@/app-info/AppInfoService'

const buildHash = __COMMIT_HASH__
const isDevClient = import.meta.env.VITE_ENV !== 'prod'
const borderBottom = { borderBottom: 1, borderColor: 'divider' }

function formatVersion(hash: string, isDev: boolean) {
  return isDev ? `dev (${hash})` : hash
}

export function UserAvatarPopoverItems() {
  const authService = useDiRef<AuthService>('AuthService')
  const cssThemeService = useDiRef<CssThemeService>('CssThemeService')
  const usersService = useDiRef<UsersService>('UsersService')
  const appInfoService = useDiRef<AppInfoService>('AppInfoService')
  const isLightTheme = useWrappedObservable(cssThemeService.isLightTheme$)
  const userData = useBehaviorObservable(usersService.user$)
  const serverInfo = useBehaviorObservable(appInfoService.serverInfo$)

  function getInitials(name: string) {
    const words = name.replace(/-/g, ' ').split(' ').filter(Boolean)
    return words.map((w) => w[0].toUpperCase()).join('')
  }

  function onLogoutClicked() {
    authService.logout()
    window.location.replace('/auth')
  }

  function toggleTheme() {
    cssThemeService.switchTheme()
  }

  const initials = userData ? getInitials(userData.name) : ''
  const avatarSize = 30

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
      {/* User info */}
      <Box
        sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 2, py: 1.5, ...borderBottom }}
      >
        <Avatar sx={{ width: avatarSize, height: avatarSize, fontSize: 13 }}>{initials}</Avatar>
        <Typography variant="body2" fontWeight={500} noWrap>
          {userData?.name ?? ''}
        </Typography>
      </Box>

      {/* Theme toggle */}
      <MenuItem onClick={toggleTheme} sx={{ width: '100%', ...borderBottom }}>
        <ListItemIcon>
          <Brightness6Icon fontSize="small" />
        </ListItemIcon>
        <ListItemText>Switch theme</ListItemText>
        <Switch
          edge="end"
          checked={!isLightTheme}
          onChange={toggleTheme}
          onClick={(e) => e.stopPropagation()}
        />
      </MenuItem>

      {/* Version info */}
      <Box
        sx={{ px: 2, py: 0.75, ...borderBottom, display: 'flex', flexDirection: 'column', gap: 0.25 }}
      >
        <Typography variant="caption" color="text.disabled">
          client: {formatVersion(buildHash, isDevClient)}
        </Typography>
        <Typography variant="caption" color="text.disabled">
          server: {serverInfo ? formatVersion(serverInfo.commit, serverInfo.env === 'dev') : '…'}
        </Typography>
      </Box>

      <MenuItem
        data-testid="popover-logout"
        onClick={onLogoutClicked}
        sx={{ color: 'error.main', width: '100%' }}
      >
        <ListItemIcon sx={{ color: 'error.main' }}>
          <LogoutIcon fontSize="small" />
        </ListItemIcon>
        Logout
      </MenuItem>
    </Box>
  )
}
